import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_PLAN, PLANS, getQuotas, type PlanId } from '../lib/plans';
import { QUOTA_CHANGED_EVENT } from '../lib/quotaErrors';

export interface QuotaItem {
  key: 'candidates' | 'missions' | 'matchings';
  label: string;
  used: number;
  /** null = illimité */
  limit: number | null;
  /** Pourcentage consommé, borné à 100. null = illimité. */
  pct: number | null;
}

export interface QuotaUsage {
  candidates: number;
  missions: number;
  matchings: number;
}

interface Snapshot {
  plan: PlanId;
  used: QuotaUsage;
}

const EMPTY_USAGE: QuotaUsage = { candidates: 0, missions: 0, matchings: 0 };

/** Premier jour du mois courant en UTC (même convention que
 *  `date_trunc('month', now())::date` côté Postgres, qui tourne en UTC). */
export function currentPeriodStart(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

export function computeQuotaItems(plan: PlanId, used: QuotaUsage): QuotaItem[] {
  const q = getQuotas(plan);
  const build = (key: QuotaItem['key'], label: string, usedVal: number, limit: number | null): QuotaItem => ({
    key,
    label,
    used: usedVal,
    limit,
    pct: limit === null ? null : limit <= 0 ? 100 : Math.min(100, Math.round((usedVal / limit) * 100)),
  });
  return [
    build('candidates', 'Candidats', used.candidates, q.candidates),
    build('missions', 'Missions / mois', used.missions, q.missionsPerMonth),
    build('matchings', 'Matchings / mois', used.matchings, q.matchingsPerMonth),
  ];
}

// Trois écrans consomment ce hook (sidebar, réglages, moteur de matching) et la
// relecture se déclenche à chaque navigation : sans mise en commun, une simple
// navigation lancerait neuf requêtes. Les instances partagent donc un même
// chargement, et une rafale de navigations ne relit pas plus d'une fois.
const DEDUPE_MS = 2000;

let cache: { userId: string; snap: Snapshot; at: number } | null = null;
let inflight: { userId: string; forced: boolean; p: Promise<Snapshot> } | null = null;

/** Vide le cache partagé entre deux tests. */
export function resetQuotaCache(): void {
  cache = null;
  inflight = null;
}

async function loadQuotas(userId: string): Promise<Snapshot> {
  // `user_plans` et `usage_counters` viennent de la migration 040. Si une lecture
  // échoue, on retombe sur le forfait par défaut et des compteurs à zéro plutôt
  // que de casser l'écran. L'absence de ligne `user_plans` est le cas nominal :
  // seul un pilote est provisionné explicitement.
  const db = supabase as any;
  const [planRes, counterRes, candidateRes] = await Promise.all([
    db.from('user_plans').select('plan').eq('user_id', userId).maybeSingle(),
    db.from('usage_counters')
      .select('missions_created, matchings_launched')
      .eq('user_id', userId)
      .eq('period', currentPeriodStart())
      .maybeSingle(),
    // Même règle que `countCandidates` côté edge functions : une analyse en échec
    // ou une pierre tombale de dédup ne consomme pas de stock. Si les deux règles
    // divergent, le compteur affiché ment sur le refus que le serveur appliquera.
    db.from('candidates')
      .select('id', { count: 'exact', head: true })
      .eq('created_by', userId)
      .not('status', 'in', '(error,duplicate)'),
  ]);
  const rawPlan = planRes?.data?.plan;
  return {
    plan: typeof rawPlan === 'string' && rawPlan in PLANS ? (rawPlan as PlanId) : DEFAULT_PLAN,
    used: {
      candidates: candidateRes?.count ?? 0,
      missions: counterRes?.data?.missions_created ?? 0,
      matchings: counterRes?.data?.matchings_launched ?? 0,
    },
  };
}

function readQuotas(userId: string, force: boolean): Promise<Snapshot> {
  if (!force && cache && cache.userId === userId && Date.now() - cache.at < DEDUPE_MS) {
    return Promise.resolve(cache.snap);
  }
  // Une relecture forcée (action de l'utilisateur) ne se raccroche pas à une
  // requête partie AVANT l'action : elle rapporterait la valeur d'avant.
  if (inflight && inflight.userId === userId && (!force || inflight.forced)) return inflight.p;

  const p = loadQuotas(userId).then(
    snap => {
      cache = { userId, snap, at: Date.now() };
      if (inflight === entry) inflight = null;
      return snap;
    },
    err => {
      if (inflight === entry) inflight = null;
      throw err;
    },
  );
  const entry = { userId, forced: force, p };
  inflight = entry;
  return p;
}

/** Forfait et consommation de l'UTILISATEUR courant — pendant la bêta les quotas
 *  sont personnels, pas partagés par l'organisation (le rattachement org
 *  reviendra avec le mode agency). */
export function useQuotas(): {
  plan: PlanId;
  loading: boolean;
  items: QuotaItem[];
  refresh: () => Promise<void>;
} {
  const { user } = useAuth();
  const location = useLocation();
  const [plan, setPlan] = useState<PlanId>(DEFAULT_PLAN);
  const [used, setUsed] = useState<QuotaUsage>(EMPTY_USAGE);
  const [loading, setLoading] = useState(true);

  const read = useCallback(async (force: boolean) => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    const snap = await readQuotas(user.id, force);
    setPlan(snap.plan);
    setUsed(snap.used);
    setLoading(false);
  }, [user?.id]);

  const refresh = useCallback(() => read(true), [read]);

  // Les consommateurs restent montés pendant toute la navigation : sans
  // relecture, l'affichage garderait la valeur du dernier chargement de page.
  // Relire à chaque changement d'URL couvre tout point de consommation, y
  // compris ceux qui oublieraient d'émettre l'événement ci-dessous.
  useEffect(() => {
    read(false);
  }, [read, location.pathname]);

  // Relecture immédiate après une action qui consomme du quota ; le retour de
  // focus rattrape le cas d'un second onglet.
  useEffect(() => {
    const onChanged = () => { read(true); };
    window.addEventListener(QUOTA_CHANGED_EVENT, onChanged);
    window.addEventListener('focus', onChanged);
    return () => {
      window.removeEventListener(QUOTA_CHANGED_EVENT, onChanged);
      window.removeEventListener('focus', onChanged);
    };
  }, [read]);

  const items = useMemo(() => computeQuotaItems(plan, used), [plan, used]);

  return { plan, loading, items, refresh };
}
